import { isEmpty } from 'lodash';
import React, { useMemo } from 'react';
import { Redirect, useParams } from 'react-router';
import { FormInstanceId, ProjectId } from 'api/models';
import { Project, ProjectDetailTabKeys } from 'api/project';
import { PageLoading } from 'components/atoms/loading';
import { HideForCustomer } from 'components/atoms/user-type-utils';
import { BreadCrumbs } from 'components/molecules/project-details/header/breadcrumbs';
import { ProjectTitle } from 'components/molecules/project-details/header/project-title';
import { ProjectDetailTabs } from 'components/molecules/project-details/header/tabs/tabs';
import { FormResponses } from 'components/organisms/projects/form-responses';
import { NoInstancesEmptyState } from 'components/organisms/projects/form-responses/empty-states';
import { ProjectDetailsPageTemplate } from 'components/templates';
import { useQueryFormInstanceByProjectKey } from 'queries/form-instance';
import { getProjectFormByInstanceUrl } from 'utils/urls';

interface ParamTypes {
  projectId: string;
  instanceId: string;
}

const FormResponsePage = ({ project }: { project: Project }) => {
  const { instanceId } = useParams<ParamTypes>();

  const { data, status } = useQueryFormInstanceByProjectKey(
    ProjectId(project.projectId),
  );

  const currentInstanceId = useMemo(() => {
    if (data && !isEmpty(data)) {
      const filteredInstance = data?.instances.find(
        (instance) => instance.formInstanceId.toString() === instanceId,
      );

      if (filteredInstance) {
        return filteredInstance.formInstanceId;
      }
    }

    return null;
  }, [data, instanceId]);

  switch (status) {
    case 'loading': {
      return <PageLoading />;
    }

    case 'success': {
      if (data && !isEmpty(data)) {
        if (currentInstanceId) {
          return (
            <FormResponses
              projectFormInstance={data}
              instanceId={currentInstanceId}
            />
          );
        }

        if (data.instances.length) {
          return (
            <Redirect
              to={getProjectFormByInstanceUrl(
                FormInstanceId(
                  project.projectId,
                  data.instances[0].formInstanceId,
                ),
              )}
            />
          );
        }
      }

      return <NoInstancesEmptyState />;
    }

    case 'idle':
    case 'error':
    default: {
      return <Redirect to="/" />;
    }
  }
};

export const FormResponseLandingPage = ({ project }: { project: Project }) => {
  return (
    <ProjectDetailsPageTemplate
      title={<ProjectTitle project={project} />}
      tabs={
        <HideForCustomer>
          <ProjectDetailTabs
            projectId={ProjectId(project.projectId)}
            activeTabKey={ProjectDetailTabKeys.FormResponses}
          />
        </HideForCustomer>
      }
      documentTitle={project.projectName}
      breadCrumbs={<BreadCrumbs />}
    >
      <FormResponsePage project={project} />
    </ProjectDetailsPageTemplate>
  );
};
